import { NodeType, TreeNode } from '../index';
import { IActionHandlerDependencies } from './types';
import { attachSampleActivityInEditMode } from '@/lib/tree/treeNodeEditFocus';
import { IBackend } from '@/api/BackendApi';
import {
  ProjectDto,
  SampleDto,
  SampleActivityDto,
  TheoryActivityDto,
  ShippingDto,
  StatusDto,
  ActivityTypeDto,
} from '@/api/ApiClient';

import { log } from '@/lib/utils/logger';
import { performDeleteOperation, handleDeleteError } from '@/lib/tree/deleteEntityUtils';
import { performSubmitOperation, handleSubmitError, refreshAfterAction } from '@/lib/tree/submitEntityUtils';
import { performResetOperation, handleResetError } from '@/lib/tree/resetEntityUtils';
import { resolveSubmitValidationForNode } from '@/lib/tree/submitValidationResolver';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';
import { getNewProcessingActivityBlockedReason } from '@/lib/utils/sampleActivityLifecycleEligibility';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { getDataPackageApiId } from '@/lib/tree/treeUtils';

/**
 * Action handler implementations
 */
export const actionHandlers = {
  // Project actions
  'add-proposal': async (node: TreeNode<'project'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add proposal action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const activity = await deps.showProjectActivityDialog(projectData, ActivityTypeDto.Proposal);
      if (activity) {
        deps.createNode('projectActivity', node.nodeId, activity);
        deps.showSnackbar('Proposal created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create proposal:', error);
    }
  },

  'add-decision': async (node: TreeNode<'project'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add decision action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const activity = await deps.showProjectActivityDialog(projectData, ActivityTypeDto.Decision);
      if (activity) {
        deps.createNode('projectActivity', node.nodeId, activity);
        deps.showSnackbar('Decision created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create decision:', error);
    }
  },

  'add-project-document': async (node: TreeNode<'project'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add project document action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const activity = await deps.showProjectActivityDialog(projectData, ActivityTypeDto.Document);
      if (activity) {
        deps.createNode('projectActivity', node.nodeId, activity);
        deps.showSnackbar('Project document created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create project document:', error);
    }
  },

  'add-report': async (node: TreeNode<'project'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add report action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const activity = await deps.showProjectActivityDialog(projectData, ActivityTypeDto.Report);
      if (activity) {
        deps.createNode('projectActivity', node.nodeId, activity);
        deps.showSnackbar('Report created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create report:', error);
    }
  },

  'add-publication': async (node: TreeNode<'project'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add publication action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const activity = await deps.showCreatePublicationDialog(projectData);
      if (activity && 'projectId' in activity) {
        deps.createNode('projectActivity', node.nodeId, activity);
        deps.showSnackbar('Publication created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create publication:', error);
    }
  },

  'add-sample': async (
    node: TreeNode<'newSamples' | 'project'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add sample action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const result = await deps.showAddToInventoryDialog(projectData);
      if (result) {
        if (result.samples.length === 1) {
          deps.showSnackbar('Sample created successfully.', 'success');
        }
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create sample:', error);
    }
  },

  'add-sample-from-startingMaterials': async (
    node: TreeNode<'newSamples' | 'project'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add sample from startingMaterials action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const sample = await deps.showCombineStartingMaterialsDialog(projectData);
      if (sample) {
        deps.showSnackbar('Sample created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create sample from startingMaterials:', error);
    }
  },

  'add-multi-sample-growth': async (
    node: TreeNode<'newSamples' | 'project'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Multi-sample growth action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const sample = await deps.showMultiSampleGrowthDialog(projectData);
      if (sample) {
        deps.showSnackbar('Multi-sample growth run created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create multi-sample growth:', error);
    }
  },

  // Sample actions
  'add-processing-activity': async (node: TreeNode<'sample'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add processing activity action selected');

    try {
      const sampleData = deps.getTypedData(node);
      if (!sampleData) {
        deps.showSnackbar('Sample data not found', 'error');
        return;
      }
      const processingBlockedReason = getNewProcessingActivityBlockedReason(sampleData);
      if (processingBlockedReason) {
        deps.showSnackbar(processingBlockedReason, 'warning');
        return;
      }
      const activity = await deps.showProcessingDialog(sampleData);
      if (activity) {
        await attachSampleActivityInEditMode(node.nodeId, activity, deps);
        deps.showSnackbar('Processing activity created successfully.', 'success');
        deps.reloadNode(node.nodeId, backend);
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create processing activity:', error);
    }
  },

  'add-char-activity': async (node: TreeNode<'sample'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add characterization activity action selected');

    try {
      const sampleData = deps.getTypedData(node);
      if (!sampleData) {
        deps.showSnackbar('Sample data not found', 'error');
        return;
      }
      const activity = await deps.showCharacterizationDialog(sampleData);
      if (activity) {
        // No reloadNode: characterization reads the sample; processing may change sample-level data (e.g. materials).
        await attachSampleActivityInEditMode(node.nodeId, activity, deps);
        deps.showSnackbar('Characterization activity created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create characterization activity:', error);
    }
  },

  'add-in-situ-char-activity': async (
    node: TreeNode<'sampleActivity'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add in-situ characterization activity action selected');

    try {
      const sampleActivity = deps.getTypedData(node);
      if (!sampleActivity) {
        deps.showSnackbar('Sample activity data not found', 'error');
        return;
      }

      // Get sample from activity or fetch from parent node.
      // Fall back to parent node if sample is missing or lacks an id
      // (the activity DTO only carries SampleLabel, not the full SampleDto).
      let sample = sampleActivity.sample;
      if ((!sample || !sample.id) && node.parentId) {
        const parentNode = deps.getNodeData(node.parentId);
        if (parentNode?.type === 'sample') {
          sample = deps.getTypedData(parentNode);
        }
      }

      if (!sample) {
        deps.showSnackbar('Sample data not found', 'error');
        return;
      }

      if (sample.shipped === true) {
        deps.showSnackbar('In-situ characterization is not available for shipped samples.', 'warning');
        return;
      }

      const activity = await deps.showCharacterizationDialog(sample, sampleActivity);
      if (activity) {
        await attachSampleActivityInEditMode(node.nodeId, activity, deps);
        deps.showSnackbar('In-situ characterization activity created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create in-situ characterization activity:', error);
    }
  },

  split: async (node: TreeNode<'sample'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Split sample action selected');

    try {
      const sampleData = deps.getTypedData(node);
      if (!sampleData) {
        deps.showSnackbar('Sample data not found', 'error');
        return;
      }
      const activity = await deps.showSplitDialog(sampleData);
      if (activity) {
        deps.createNode('sampleActivity', node.nodeId, activity);
        deps.showSnackbar('Sample split successfully.', 'success');
        deps.onClose?.();
        deps.reloadNode(node.nodeId, backend);
      }
    } catch (error) {
      log.error('[NodeActions] Failed to split sample:', error);
    }
  },

  'add-storage': async (node: TreeNode<'sample'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Add storage action selected');

    try {
      const sampleData = deps.getTypedData(node);
      if (!sampleData) {
        deps.showSnackbar('Sample data not found', 'error');
        return;
      }
      const activity = await deps.showStorageDialog(sampleData);
      if (activity) {
        deps.createNode('sampleActivity', node.nodeId, activity);
        deps.showSnackbar('Storage activity created successfully.', 'success');
        deps.onClose?.();
        deps.reloadNode(node.nodeId, backend);
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create storage activity:', error);
    }
  },

  transfer: async (node: TreeNode<'sample'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Transfer sample action selected');

    try {
      const sampleData = deps.getTypedData(node);
      if (!sampleData) {
        deps.showSnackbar('Sample data not found', 'error');
        return;
      }
      // Let the dialog perform the transfer (and create an activity if configured).
      // We no longer depend on activity IDs here – instead we always refresh the subtree
      // so the tree reflects whatever activities the backend created.
      await deps.showTransferActivityDialog(sampleData);

      // After transfer completes, the backend state for this sample (including its activities)
      // may have changed. To avoid showing stale children we:
      // 1) Invalidate any cached children data for this node
      // 2) Clear the "children loaded" flag
      // 3) Load the children fresh from the backend
      // 4) Reload the sample node itself so its summary data is up to date
      deps.invalidateChildrenCache(node.nodeId);
      deps.clearChildrenLoaded(node.nodeId);
      await deps.loadChildren(node.nodeId, backend);
      await deps.reloadNode(node.nodeId, backend);

      deps.onClose?.();
    } catch (error) {
      log.error('[NodeActions] Failed to create transfer activity:', error);
      deps.showSnackbar('Failed to create transfer activity', 'error');
    }
  },

  // Communication actions
  'add-communication': async (
    node: TreeNode<'communications' | 'project'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add communication action selected');

    try {
      const projectData = deps.getTypedData(node);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }
      const communication = await deps.showCommunicationDialog(projectData);
      if (communication) {
        deps.createNode('communication', node.nodeId, communication);
        deps.showSnackbar('Communication created successfully.', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create communication:', error);
    }
  },

  // Theory activity actions
  'add-theory-activity': async (
    node: TreeNode<'simulationDataset'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add simulation action selected');

    // For simulation datasets, use the existing project-based dialog
    try {
      const dataPackageData = deps.getTypedData(node);
      if (!dataPackageData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }

      const activity = await deps.showAddTheoryActivityDialog(dataPackageData);
      if (activity) {
        deps.createNode('theoryActivity', node.nodeId, activity);
        deps.showSnackbar('Theory activity created successfully', 'success');
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create theory activity:', error);
      deps.showSnackbar('Failed to create theory activity', 'error');
    }
  },

  // Data package actions
  'add-data-package': async (
    node: TreeNode<'project' | 'simulations'>,
    _backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add data package action selected');
    const projectData = deps.getTypedData(node);
    if (!projectData) {
      deps.showSnackbar('Project data not found', 'error');
      return;
    }
    const simulationResult = await deps.showAddSimulationDatasetDialog(projectData);
    if (simulationResult) {
      deps.createNode('simulationDataset', node.nodeId, simulationResult);
    }
  },

  // Other actions
  ship: async (node: TreeNode<'shippings'>, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Ship action selected');

    try {
      // Get the project from the parent node (shippings container is under project)
      const parentNode = deps.getNodeData(node.parentId!);
      if (!parentNode || parentNode.type !== 'project') {
        deps.showSnackbar('Could not find project for shipping', 'error');
        return;
      }

      const projectData = deps.getTypedData(parentNode);
      if (!projectData) {
        deps.showSnackbar('Project data not found', 'error');
        return;
      }

      const shipping = await deps.showAddShippingDialog(projectData, { launchFrom: 'projectTree' });
      if (shipping) {
        deps.createNode('shipping', node.nodeId, shipping);
        deps.showSnackbar(
          shipping.localTransfer ? 'Sample transfer created successfully.' : 'Shipping created successfully.',
          'success'
        );
        deps.onClose?.();
      }
    } catch (error) {
      log.error('[NodeActions] Failed to create shipping:', error);
      deps.showSnackbar('Failed to create shipping', 'error');
    }
  },

  duplicate: async (_node: TreeNode, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Duplicate action selected');
    deps.showSnackbar('Duplicate not yet supported for this type', 'warning');
  },

  delete: async (
    node: TreeNode<
      | 'sample'
      | 'sampleActivity'
      | 'project'
      | 'publication'
      | 'projectActivity'
      | 'theoryActivity'
      | 'communication'
      | 'dataPackage'
      | 'shipping'
    >,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Delete action selected');

    try {
      // Use the existing delete dialog and logic
      const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, (dataId) => {
        if (dataId === node.dataId) {
          return deps.getTypedData(node);
        }
        const foundNode = deps.getNodeData(dataId);
        return foundNode ? deps.getTypedData(foundNode) : null;
      });
      const confirmed = await deps.showDeleteDialog(entityTypeLabel, node.label);
      if (!confirmed) {
        return;
      }

      try {
        // First attempt without force
        await performDeleteOperation(node, backend, false);
        deps.deleteNode(node.nodeId); // Remove from tree
        deps.showSnackbar(`${entityTypeLabel} ${node.label} deleted successfully`, 'success');
        deps.onClose?.();
      } catch (error) {
        // Handle specific error cases that might require confirmation
        const errorResult = await handleDeleteError(error, node, {
          showSnackbar: deps.showSnackbar,
          showConfirmDialog: deps.showConfirmDialog,
          getData: (dataId) => {
            // Use the current node's data if dataId matches
            if (dataId === node.dataId) {
              return deps.getTypedData(node);
            }
            // Try to find node by dataId (simplified - in practice might need tree traversal)
            // For now, return null if not found
            return null;
          },
        });

        if (errorResult.shouldRetry) {
          // Retry with force
          await performDeleteOperation(node, backend, true);
          deps.deleteNode(node.nodeId); // Remove from tree
          deps.showSnackbar(`${entityTypeLabel} ${node.label} deleted successfully`, 'success');
          deps.onClose?.();
        }
        // If shouldAbort is true, error handling already showed appropriate message
      }
    } catch (error) {
      log.error('[NodeActions] Failed to delete:', error);
      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to delete.', 'error');
    }
  },

  submit: async (
    node: TreeNode<'sample' | 'sampleActivity' | 'projectActivity' | 'theoryActivity' | 'dataPackage' | 'shipping'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Submit action selected');

    try {
      const validation = await resolveSubmitValidationForNode(node, backend, deps.getTypedData);
      if (!validation.isValid) {
        log.warn(`[NodeActions] ${validation.entityLabel} validation failed:`, validation.errors);
        deps.showSnackbar(
          `Cannot submit ${validation.entityLabel.toLowerCase()}. Please complete the following fields: ${validation.errors.join(', ')}`,
          'warning'
        );
        return;
      }

      const confirmed = await deps.showSubmitDialog(TreeNodeFactory.getNodeTypeLabel(node), node.label);
      if (!confirmed) {
        return;
      }

      try {
        // First attempt with userHasConfirmed=false
        await performSubmitOperation(node, backend, false);
        await refreshAfterAction(
          node,
          backend,
          {
            reloadNode: deps.reloadNode,
            clearChildrenLoaded: deps.clearChildrenLoaded,
            invalidateChildrenCache: deps.invalidateChildrenCache,
            loadChildren: deps.loadChildren,
            expandNode: deps.expandNode,
          },
          { reloadNodeChildrenWhenSample: true }
        );
        const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, (dataId) => {
          if (dataId === node.dataId) {
            return deps.getTypedData(node);
          }
          const foundNode = deps.getNodeData(dataId);
          return foundNode ? deps.getTypedData(foundNode) : null;
        });
        deps.showSnackbar(`${entityTypeLabel} ${node.label} submitted successfully`, 'success');
        deps.onClose?.();
      } catch (error) {
        // Handle specific error cases that might require confirmation
        const errorResult = await handleSubmitError(error, node, {
          showSnackbar: deps.showSnackbar,
          showConfirmDialog: deps.showConfirmDialog,
          getData: (dataId) => {
            if (dataId === node.dataId) {
              return deps.getTypedData(node);
            }
            return null;
          },
        });

        if (errorResult.shouldRetry) {
          // Retry with userHasConfirmed=true after the user confirms the backend warning.
          await performSubmitOperation(node, backend, true);
          await refreshAfterAction(
            node,
            backend,
            {
              reloadNode: deps.reloadNode,
              clearChildrenLoaded: deps.clearChildrenLoaded,
              invalidateChildrenCache: deps.invalidateChildrenCache,
              loadChildren: deps.loadChildren,
              expandNode: deps.expandNode,
            },
            { reloadNodeChildrenWhenSample: true }
          );
          const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, (dataId) => {
            if (dataId === node.dataId) {
              return deps.getTypedData(node);
            }
            return null;
          });
          deps.showSnackbar(`${entityTypeLabel} ${node.label} submitted successfully`, 'success');
          deps.onClose?.();
        }
        // If shouldAbort is true, error handling already showed appropriate message
      }
    } catch (error) {
      log.error('[NodeActions] Failed to submit:', error);
      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to submit.', 'error');
    }
  },

  reset: async (
    node: TreeNode<'sample' | 'sampleActivity' | 'projectActivity' | 'theoryActivity' | 'dataPackage' | 'shipping'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Reset action selected');

    try {
      const confirmed = await deps.showResetDialog(TreeNodeFactory.getNodeTypeLabel(node), node.label);
      if (!confirmed) {
        return;
      }

      try {
        await performResetOperation(node, backend);
        await refreshAfterAction(
          node,
          backend,
          {
            reloadNode: deps.reloadNode,
            clearChildrenLoaded: deps.clearChildrenLoaded,
            invalidateChildrenCache: deps.invalidateChildrenCache,
            loadChildren: deps.loadChildren,
            expandNode: deps.expandNode,
          },
          { reloadParentSubtree: true }
        );
        const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, (dataId) => {
          if (dataId === node.dataId) {
            return deps.getTypedData(node);
          }
          const foundNode = deps.getNodeData(dataId);
          return foundNode ? deps.getTypedData(foundNode) : null;
        });
        deps.showSnackbar(`${entityTypeLabel} ${node.label} reset successfully`, 'success');
        deps.onClose?.();
      } catch (error) {
        // Handle specific error cases that might require confirmation
        const errorResult = await handleResetError(error, node, {
          showSnackbar: deps.showSnackbar,
          showConfirmDialog: deps.showConfirmDialog,
          getData: (dataId) => {
            if (dataId === node.dataId) {
              return deps.getTypedData(node);
            }
            return null;
          },
        });

        if (errorResult.shouldRetry) {
          // Note: Reset typically doesn't have a force parameter, but we handle 428 for consistency
          // If backend supports force reset, we'd call it here
          await performResetOperation(node, backend);
          await refreshAfterAction(
            node,
            backend,
            {
              reloadNode: deps.reloadNode,
              clearChildrenLoaded: deps.clearChildrenLoaded,
              invalidateChildrenCache: deps.invalidateChildrenCache,
              loadChildren: deps.loadChildren,
              expandNode: deps.expandNode,
            },
            { reloadParentSubtree: true }
          );
          const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, (dataId) => {
            if (dataId === node.dataId) {
              return deps.getTypedData(node);
            }
            return null;
          });
          deps.showSnackbar(`${entityTypeLabel} ${node.label} reset successfully`, 'success');
          deps.onClose?.();
        }
        // If shouldAbort is true, error handling already showed appropriate message
      }
    } catch (error) {
      log.error('[NodeActions] Failed to reset:', error);
      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to reset.', 'error');
    }
  },

  reload: async (node: TreeNode<NodeType>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Reload action selected');

    try {
      if (deps.hasModifiedDataInSubtree(node.nodeId)) {
        const forceReload = await deps.showConfirmDialog({
          title: 'Unsaved changes',
          message:
            'You have unsaved changes in this tree. Force reload will discard them. Save your changes first, or continue to reload anyway?',
          confirmText: 'Force reload',
          isDestructive: true,
        });
        if (!forceReload) {
          deps.onClose?.();
          return;
        }
      }

      // Explicit Reload = bypass all caches, fetch from DB
      deps.invalidateChildrenCache(node.nodeId);
      await deps.reloadNode(node.nodeId, backend);
      deps.clearChildrenLoaded(node.nodeId);
      await deps.loadChildren(node.nodeId, backend);
      deps.expandNode(node.nodeId);

      deps.showSnackbar(`${node.label} reloaded successfully`, 'success');
      deps.onClose?.();
    } catch (error) {
      log.error('[NodeActions] Failed to reload:', error);
      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to reload.', 'error');
    }
  },

  'view-data': async (_node: TreeNode, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] View data action selected');
    deps.showSnackbar('View data functionality not yet implemented', 'info');
    deps.onClose?.();
  },

  share: async (_node: TreeNode, _backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Share action selected');
    deps.showSnackbar('Share functionality not yet implemented', 'info');
    deps.onClose?.();
  },

  download: async (node: TreeNode, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Download action selected');
    deps.onClose?.();

    try {
      const nodeData = deps.getTypedData(node);
      if (!nodeData || !nodeData.id) {
        deps.showSnackbar('Node data not found', 'error');
        return;
      }

      // Map node types to backend API calls
      let linkResultPromise: Promise<any> | null = null;
      let pollFunction: (() => Promise<any>) | undefined = undefined;

      switch (node.type) {
        case 'project': {
          const project = nodeData as ProjectDto;
          const idOrLabel = project.id?.toString() || project.projectLabel || '';
          linkResultPromise = backend.project.createFolderLink(idOrLabel);
          pollFunction = () => backend.project.createFolderLink(idOrLabel);
          break;
        }
        case 'dataPackage': {
          const idOrDoi = getDataPackageApiId(node.dataId);
          linkResultPromise = backend.data.createFolderLink(idOrDoi);
          pollFunction = () => backend.data.createFolderLink(idOrDoi);
          break;
        }
        case 'sample': {
          const sample = nodeData as SampleDto;
          const idOrLabel = sample.id?.toString() || sample.sampleLabel || '';
          linkResultPromise = backend.sample.createFolderLink(idOrLabel, null);
          pollFunction = () => backend.sample.createFolderLink(idOrLabel, null);
          break;
        }
        case 'sampleActivity': {
          const activity = nodeData as SampleActivityDto;
          linkResultPromise = backend.sampleActivity.getFolderLink(activity.id!);
          pollFunction = () => backend.sampleActivity.getFolderLink(activity.id!);
          break;
        }
        case 'theoryActivity': {
          const activity = nodeData as TheoryActivityDto;
          linkResultPromise = backend.theoryActivity.createFolderLink(activity.id!);
          pollFunction = () => backend.theoryActivity.createFolderLink(activity.id!);
          break;
        }
        case 'shipping': {
          const shipping = nodeData as ShippingDto;
          linkResultPromise = backend.shipping.createFolderLink(shipping.id!);
          pollFunction = () => backend.shipping.createFolderLink(shipping.id!);
          break;
        }
        default:
          deps.showSnackbar(`Download functionality not available for ${node.type}`, 'info');
          return;
      }

      if (!linkResultPromise) {
        return;
      }

      // Get initial link result (with recreate=true equivalent - by calling the API)
      const initialLinkResult = await linkResultPromise;

      // Show dialog with the link result
      await deps.showDownloadFolderDialog(node.label, initialLinkResult, pollFunction);
    } catch (error: any) {
      log.error('[NodeActions] Error downloading folder:', error);
      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to download folder', 'error');
    }
  },

  // Data Package Specific Actions - Not implemented yet
  'create-snapshot': async (node: TreeNode<'dataPackage'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Create snapshot action selected');
    deps.onClose?.();

    try {
      const dataPackage = deps.getTypedData(node);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      // Show confirmation dialog
      const confirmed = await deps.showConfirmDialog({
        title: 'Create Snapshot',
        message: `Do you want to prepare this data package for publication and create a snapshot?\n\nA copy of all sample and activity data will be created, and only those copies will be accessible via this data package. Any changes made to samples contained in this package will not be reflected in the data package. You can still make changes to the snapshot afterwards, like removing characterization data that shall not (yet) be published.`,
        confirmText: 'Create Snapshot',
        isDestructive: false,
      });

      if (!confirmed) {
        return;
      }

      deps.markLoading(node.dataId, true);

      try {
        await backend.data.createSnapshot(dataPackage.id);
        deps.showSnackbar('Snapshot creation started. Progress will be shown in the status footer.', 'success');

        // Reload the data package node to get updated state (isSnapshot, percentSnapshotDone, etc.)
        await deps.reloadNode(node.nodeId, backend);
        // Clear and reload children since they may have changed type (e.g., sample -> sampleSnapshot)
        deps.clearChildrenLoaded(node.nodeId);
        await deps.loadChildren(node.nodeId, backend);
      } catch (error) {
        log.error('[NodeActions] Error creating snapshot:', error);
        deps.showSnackbar(getProblemDetail(error) ?? 'Failed to create snapshot.', 'error');
      } finally {
        deps.markLoading(node.dataId, false);
      }
    } catch (error) {
      log.error('[NodeActions] Error in create-snapshot handler:', error);
      deps.showSnackbar('Failed to create snapshot', 'error');
    }
  },

  'restart-snapshot': async (node: TreeNode<'dataPackage'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Restart stalled snapshot action selected');
    deps.onClose?.();

    try {
      const dataPackage = deps.getTypedData(node);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      const confirmed = await deps.showConfirmDialog({
        title: 'Resume stalled snapshot',
        message: `Snapshot creation looks stuck (no progress for a while). The server will queue the snapshot job again.\n\nIf a job is still running, wait until it stops updating before resuming.`,
        confirmText: 'Resume snapshot',
        isDestructive: false,
      });

      if (!confirmed) {
        return;
      }

      deps.markLoading(node.dataId, true);

      try {
        await backend.data.restartSnapshot(dataPackage.id);
        deps.showSnackbar('Snapshot build resumed. Progress will update in the status footer.', 'success');
        await deps.reloadNode(node.nodeId, backend);
        deps.clearChildrenLoaded(node.nodeId);
        await deps.loadChildren(node.nodeId, backend);
      } catch (error) {
        log.error('[NodeActions] Error restarting snapshot:', error);
        deps.showSnackbar(getProblemDetail(error) ?? 'Failed to resume snapshot.', 'error');
      } finally {
        deps.markLoading(node.dataId, false);
      }
    } catch (error) {
      log.error('[NodeActions] Error in restart-snapshot handler:', error);
      deps.showSnackbar('Failed to resume snapshot', 'error');
    }
  },

  'add-sample-to-data-package': async (
    node: TreeNode<'dataPackage'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add sample to data package action selected');
    deps.onClose?.();

    try {
      const dataPackage = deps.getTypedData(node);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      const samples = await deps.showSampleSelectionDialog('Add Samples to Data Package', dataPackage.sampleIds ?? []);

      if (samples && samples.length > 0) {
        deps.markLoading(node.dataId, true);

        try {
          const sampleLabels = samples.map((s) => s.sampleLabel || s.id?.toString()).filter(Boolean) as string[];
          const addedSamples = await backend.data.addSamples(dataPackage.id, sampleLabels);

          if (addedSamples && addedSamples.length > 0) {
            deps.showSnackbar(`Added ${addedSamples.length} sample(s) to data package`, 'success');
            // Reload the data package so sampleIds and children reflect the new state
            await deps.reloadNode(node.nodeId, backend);
            deps.invalidateChildrenCache(node.nodeId);
            deps.clearChildrenLoaded(node.nodeId);
            await deps.loadChildren(node.nodeId, backend);
            deps.expandNode(node.nodeId);
          } else {
            deps.showSnackbar('Selected samples were already in the data package', 'info');
          }
        } finally {
          deps.markLoading(node.dataId, false);
        }
      }
    } catch (error) {
      log.error('[NodeActions] Error adding samples to data package:', error);

      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to add samples to data package.', 'error');
    }
  },

  'add-theory-activity-to-data-package': async (
    node: TreeNode<'dataPackage'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Add simulation to data package action selected');
    deps.onClose?.();

    try {
      const dataPackage = deps.getTypedData(node);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      const activities = await deps.showTheoryActivitySelectionDialog('Add Simulations to Data Package');

      if (activities && activities.length > 0) {
        deps.markLoading(node.dataId, true);

        try {
          let addedCount = 0;
          for (const activity of activities) {
            try {
              await backend.data.addActivity(dataPackage.id, activity.id!);
              addedCount++;
            } catch (error) {
              log.error('[NodeActions] Failed to add activity:', activity.id, error);
            }
          }

          if (addedCount > 0) {
            deps.showSnackbar(`Added ${addedCount} simulation(s) to data package`, 'success');
            deps.invalidateChildrenCache(node.nodeId);
            deps.clearChildrenLoaded(node.nodeId);
            await deps.loadChildren(node.nodeId, backend);
            deps.expandNode(node.nodeId);
          } else if (activities.length > 0) {
            deps.showSnackbar('Selected simulations were already in the data package', 'info');
          }
        } finally {
          deps.markLoading(node.dataId, false);
        }
      }
    } catch (error) {
      log.error('[NodeActions] Error adding simulations to data package:', error);
      deps.showSnackbar('Failed to add simulations to data package', 'error');
    }
  },

  'remove-from-snapshot': async (
    node: TreeNode<'sampleSnapshot' | 'sampleActivitySnapshot'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Remove from snapshot action selected');
    deps.onClose?.();

    try {
      const nodeData = deps.getTypedData(node);
      if (!nodeData || !nodeData.id) {
        deps.showSnackbar('Item data not found', 'error');
        return;
      }

      // Find the parent data package node by traversing up the tree
      let currentNodeId: string | undefined = node.parentId;
      let dataPackageNode: TreeNode<'dataPackage'> | null = null;

      while (currentNodeId) {
        const parentNode = deps.getNodeData(currentNodeId);
        if (!parentNode) break;

        if (parentNode.type === 'dataPackage' || parentNode.type === 'simulationDataset') {
          dataPackageNode = parentNode as TreeNode<'dataPackage'>;
          break;
        }

        currentNodeId = parentNode.parentId;
      }

      if (!dataPackageNode) {
        deps.showSnackbar('Could not find parent data package', 'error');
        return;
      }

      const dataPackage = deps.getTypedData(dataPackageNode);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      // Check if snapshot is in progress
      if (
        dataPackage.isSnapshot &&
        dataPackage.percentSnapshotDone !== undefined &&
        dataPackage.percentSnapshotDone < 100
      ) {
        deps.showSnackbar('Cannot remove items while snapshot is being created', 'warning');
        return;
      }

      // Confirm removal
      const confirmed = await deps.showConfirmDialog({
        title: 'Remove from Snapshot',
        message: `Are you sure you want to remove this ${node.type === 'sampleSnapshot' ? 'sample' : 'activity'} from the snapshot?`,
        confirmText: 'Remove',
        isDestructive: true,
      });

      if (!confirmed) return;

      deps.markLoading(node.dataId, true);
      try {
        if (node.type === 'sampleSnapshot') {
          const sampleData = nodeData as SampleDto;
          const sampleLabel = sampleData.sampleLabel || sampleData.id?.toString();
          if (!sampleLabel) {
            deps.showSnackbar('Sample label not found', 'error');
            return;
          }
          await backend.data.removeSample(dataPackage.id, sampleLabel);
          deps.showSnackbar('Sample removed from snapshot', 'success');
        } else if (node.type === 'sampleActivitySnapshot') {
          const activityData = nodeData as SampleActivityDto;
          await backend.data.removeSampleActivities(dataPackage.id, activityData.id!);
          deps.showSnackbar('Activity removed from snapshot', 'success');
        }

        // Remove the node from the tree
        await deps.deleteNode(node.nodeId);
      } finally {
        deps.markLoading(node.dataId, false);
      }
    } catch (error) {
      log.error('[NodeActions] Error removing from snapshot:', error);
      deps.showSnackbar('Failed to remove from snapshot', 'error');
    }
  },

  'recreate-snapshot-from-live': async (
    node: TreeNode<'sampleSnapshot' | 'sampleActivitySnapshot'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Recreate snapshot from live data action selected');
    deps.onClose?.();

    try {
      const nodeData = deps.getTypedData(node);
      if (!nodeData || !nodeData.id) {
        deps.showSnackbar('Item data not found', 'error');
        return;
      }

      let currentNodeId: string | undefined = node.parentId;
      let dataPackageNode: TreeNode<'dataPackage'> | null = null;

      while (currentNodeId) {
        const parentNode = deps.getNodeData(currentNodeId);
        if (!parentNode) break;

        if (parentNode.type === 'dataPackage' || parentNode.type === 'simulationDataset') {
          dataPackageNode = parentNode as TreeNode<'dataPackage'>;
          break;
        }

        currentNodeId = parentNode.parentId;
      }

      if (!dataPackageNode) {
        deps.showSnackbar('Could not find parent data package', 'error');
        return;
      }

      const dataPackage = deps.getTypedData(dataPackageNode);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      if (!dataPackage.isSnapshot) {
        deps.showSnackbar('Recreate from live is only available inside a snapshot data package', 'warning');
        return;
      }

      if (dataPackage.status === StatusDto.Published) {
        deps.showSnackbar('Cannot refresh snapshot copies in a published data package', 'warning');
        return;
      }

      if (dataPackage.percentSnapshotDone !== undefined && dataPackage.percentSnapshotDone < 100) {
        deps.showSnackbar('Cannot refresh while snapshot creation is still in progress', 'warning');
        return;
      }

      const entityLabel = node.type === 'sampleSnapshot' ? 'sample' : 'activity';
      const confirmed = await deps.showConfirmDialog({
        title: 'Recreate from Live Data',
        message: `Replace this snapshot ${entityLabel} copy with the current live ${entityLabel} data? Unpublished snapshot only.`,
        confirmText: 'Recreate',
        isDestructive: false,
      });

      if (!confirmed) return;

      deps.markLoading(node.dataId, true);
      try {
        if (node.type === 'sampleSnapshot') {
          const sampleData = nodeData as SampleDto;
          const sampleId = sampleData.id;
          if (sampleId == null) {
            deps.showSnackbar('Sample id not found', 'error');
            return;
          }
          await backend.data.refreshSampleInSnapshot(dataPackage.id, sampleId);
        } else {
          const activityData = nodeData as SampleActivityDto;
          const activityId = activityData.id;
          if (activityId == null) {
            deps.showSnackbar('Activity id not found', 'error');
            return;
          }
          await backend.data.refreshActivityInSnapshot(dataPackage.id, activityId);
        }

        await deps.reloadNode(node.nodeId, backend);
        deps.showSnackbar('Snapshot copy refresh completed.', 'success');
      } finally {
        deps.markLoading(node.dataId, false);
      }
    } catch (error) {
      log.error('[NodeActions] Error refreshing snapshot from live:', error);
      deps.showSnackbar(getProblemDetail(error) ?? 'Failed to recreate snapshot copy from live data.', 'error');
    }
  },

  'publish-data-package': async (
    node: TreeNode<'dataPackage'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Publish data package action selected');
    deps.onClose?.();

    try {
      const dataPackage = deps.getTypedData(node);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      const isSnapshot = dataPackage.isSnapshot;
      const confirmMessage = isSnapshot
        ? 'This will publish the snapshot. After publishing, only DOI can be added. Continue?'
        : 'This will make the data package publicly accessible with current live data. Continue?';

      const confirmed = await deps.showConfirmDialog({
        title: 'Publish Data Package',
        message: confirmMessage,
        confirmText: 'Publish',
        isDestructive: false,
      });

      if (!confirmed) return;

      deps.markLoading(node.dataId, true);
      try {
        await backend.data.publish(dataPackage.id);
        await deps.reloadNode(node.nodeId, backend);
        deps.showSnackbar('Data package published successfully', 'success');
      } finally {
        deps.markLoading(node.dataId, false);
      }
    } catch (error) {
      log.error('[NodeActions] Error publishing data package:', error);
      deps.showSnackbar('Failed to publish data package', 'error');
    }
  },

  'set-doi': async (node: TreeNode<'dataPackage'>, backend: IBackend, deps: IActionHandlerDependencies) => {
    log.debug('[NodeActions] Set DOI action selected');
    deps.onClose?.();

    try {
      const dataPackage = deps.getTypedData(node);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      const result = await deps.showDoiDialog(dataPackage);
      if (result) {
        deps.markLoading(node.dataId, true);
        try {
          await backend.data.setDoi(dataPackage.id.toString(), {
            dataPackageDoi: result.dataPackageDoi,
            publicationDoi: result.publicationDoi,
          });
          await deps.reloadNode(node.nodeId, backend);
          deps.showSnackbar('DOI set successfully', 'success');
        } finally {
          deps.markLoading(node.dataId, false);
        }
      }
    } catch (error) {
      log.error('[NodeActions] Error setting DOI:', error);
      deps.showSnackbar('Failed to set DOI', 'error');
    }
  },

  'remove-from-data-package': async (
    node: TreeNode<'sampleInPackage' | 'theoryActivityInPackage'>,
    backend: IBackend,
    deps: IActionHandlerDependencies
  ) => {
    log.debug('[NodeActions] Remove from data package action selected');
    deps.onClose?.();

    try {
      const nodeData = deps.getTypedData(node);
      if (!nodeData || !nodeData.id) {
        deps.showSnackbar('Item data not found', 'error');
        return;
      }

      // Find the parent data package node by traversing up the tree
      let currentNodeId: string | undefined = node.parentId;
      let dataPackageNode: TreeNode<'dataPackage'> | null = null;

      while (currentNodeId) {
        const parentNode = deps.getNodeData(currentNodeId);
        if (!parentNode) break;

        // Check if this node is a data package
        if (parentNode.type === 'dataPackage' || parentNode.type === 'simulationDataset') {
          dataPackageNode = parentNode as TreeNode<'dataPackage'>;
          break;
        }

        // Move up to the next parent
        currentNodeId = parentNode.parentId;
      }

      if (!dataPackageNode) {
        deps.showSnackbar('Could not find parent data package', 'error');
        return;
      }

      const dataPackage = deps.getTypedData(dataPackageNode);
      if (!dataPackage || !dataPackage.id) {
        deps.showSnackbar('Data package not found', 'error');
        return;
      }

      // Check if data package is published or submitted
      if (dataPackage.status === StatusDto.Published || dataPackage.status === StatusDto.Submitted || dataPackage.doi) {
        deps.showSnackbar('Cannot remove items from a published or submitted data package', 'warning');
        return;
      }

      // Check if snapshot is in progress
      if (
        dataPackage.isSnapshot &&
        dataPackage.percentSnapshotDone !== undefined &&
        dataPackage.percentSnapshotDone < 100
      ) {
        deps.showSnackbar('Cannot remove items while snapshot is being created', 'warning');
        return;
      }

      // Perform the removal based on node type
      if (node.type === 'sampleInPackage') {
        const sampleData = nodeData as SampleDto;
        const sampleLabel = sampleData.sampleLabel || sampleData.id?.toString();
        if (!sampleLabel) {
          deps.showSnackbar('Sample label not found', 'error');
          return;
        }
        await backend.data.removeSample(dataPackage.id, sampleLabel);
        deps.showSnackbar(`Sample removed from data package`, 'success');
      } else if (node.type === 'theoryActivityInPackage') {
        const theoryActivity = nodeData as TheoryActivityDto;
        await backend.data.removeTheoryActivities(dataPackage.id, theoryActivity.id!);
        deps.showSnackbar(`Simulation removed from data package`, 'success');
      }

      await deps.deleteNode(node.nodeId);
      deps.invalidateChildrenCache(dataPackageNode.nodeId);
    } catch (error) {
      log.error('[NodeActions] Error removing from data package:', error);
      deps.showSnackbar('Failed to remove from data package', 'error');
    }
  },
};
